import { z } from "zod";
import { CheckResultSchema } from "./quality-gate-result.js";
export const ReleaseLabelSchema = z.enum([
    "FULLY_GATED",
    "CONDITIONALLY_RELEASED",
    "ADVISORY_INCOMPLETE",
    "BLOCKED",
]);
export const CheckClassSchema = z.enum(["CLASS_A", "CLASS_B", "CLASS_C"]);
export const CheckResultV1Schema = CheckResultSchema.extend({
    checkClass: CheckClassSchema,
    overridden: z.boolean().default(false),
    excepted: z.boolean().default(false),
    disabled: z.boolean().default(false),
    skipped: z.boolean().default(false),
});
export const ExceptionRecordV1Schema = z
    .object({
    schemaVersion: z.literal("exception-record.v1.0"),
    checkId: z.string().min(1),
    reason: z.string().min(1),
    approver: z.string().min(1),
    approvedAt: z.string().datetime(),
    impact: z.string().min(1),
})
    .strict();
export const DisabledCheckRecordV1Schema = z
    .object({
    schemaVersion: z.literal("disabled-check-record.v1.0"),
    checkId: z.string().min(1),
    reason: z.string().min(1),
    disabledByPolicy: z.string().min(1),
    disabledAt: z.string().datetime(),
})
    .strict();
export const SkippedCheckRecordV1Schema = z
    .object({
    schemaVersion: z.literal("skipped-check-record.v1.0"),
    checkId: z.string().min(1),
    reason: z.string().min(1),
    skippedAt: z.string().datetime(),
})
    .strict();
export const ProofGateGovernancePolicyV1Schema = z
    .object({
    schemaVersion: z.literal("proof-gate-governance-policy.v1.0"),
    policyVersion: z.string().min(1),
    classAOverrideForbidden: z.literal(true),
    classBExceptionCap: z.number().int().min(0).default(3),
    classCMustBeVisibleWhenDisabled: z.literal(true),
})
    .strict();
export const DEFAULT_PROOF_GATE_GOVERNANCE_POLICY = ProofGateGovernancePolicyV1Schema.parse({
    schemaVersion: "proof-gate-governance-policy.v1.0",
    policyVersion: "ProofGateGovernancePolicyV1",
    classAOverrideForbidden: true,
    classBExceptionCap: 3,
    classCMustBeVisibleWhenDisabled: true,
});
export const QAGatedStatusV1Schema = z
    .object({
    schemaVersion: z.literal("qa-gated-status.v1.0"),
    releaseLabel: ReleaseLabelSchema,
    checks: z.array(CheckResultV1Schema),
    exceptions: z.array(ExceptionRecordV1Schema).default([]),
    disabledChecks: z.array(DisabledCheckRecordV1Schema).default([]),
    skippedChecks: z.array(SkippedCheckRecordV1Schema).default([]),
    computedAt: z.string().datetime(),
    policyVersion: z.string().min(1),
})
    .strict();
const BLOCKING_SEVERITIES = new Set(["BLOCKING", "SECURITY", "FAIL"]);
function isBlockingFailure(check) {
    return !check.passed && BLOCKING_SEVERITIES.has(check.severity);
}
function isWarnFailure(check) {
    return !check.passed && check.severity === "WARN";
}
function isGovernanceExcluded(check) {
    return check.excepted || check.disabled || check.skipped;
}
/** CLASS_A checks can never be overridden, excepted, or disabled. */
export function assertClassAGovernance(check) {
    if (check.checkClass !== "CLASS_A")
        return;
    if (check.overridden || check.excepted || check.disabled || check.skipped) {
        throw new Error(`CLASS_A check ${check.checkId} cannot be overridden, excepted, disabled, or skipped`);
    }
}
export function computeReleaseLabel(input) {
    const policy = input.policy ?? DEFAULT_PROOF_GATE_GOVERNANCE_POLICY;
    const { checks, exceptions, disabledChecks, skippedChecks } = input;
    for (const check of checks) {
        assertClassAGovernance(check);
    }
    const unresolvedBlocking = checks.filter((check) => isBlockingFailure(check) && !isGovernanceExcluded(check));
    const exceptedBlockingFailures = checks.filter((check) => isBlockingFailure(check) && check.excepted);
    const exceptedClassBBlocking = exceptedBlockingFailures.filter((check) => check.checkClass === "CLASS_B");
    const unresolvedClassBBlocking = unresolvedBlocking.filter((check) => check.checkClass === "CLASS_B");
    const hasDisabledOrSkippedFlags = checks.some((check) => check.disabled || check.skipped);
    const hasWarnFailures = checks.some(isWarnFailure);
    const exceptionsOverCap = exceptions.length > policy.classBExceptionCap;
    if (unresolvedBlocking.some((check) => check.checkClass === "CLASS_A")) {
        return "BLOCKED";
    }
    if (exceptionsOverCap &&
        (unresolvedClassBBlocking.length > 0 || exceptedClassBBlocking.length > 0)) {
        return "BLOCKED";
    }
    if (exceptedClassBBlocking.length > 0) {
        return "CONDITIONALLY_RELEASED";
    }
    if (exceptions.length > 0) {
        return "CONDITIONALLY_RELEASED";
    }
    if (unresolvedClassBBlocking.length > 0) {
        return "CONDITIONALLY_RELEASED";
    }
    if (unresolvedBlocking.length > 0 ||
        hasDisabledOrSkippedFlags ||
        disabledChecks.length > 0 ||
        skippedChecks.length > 0 ||
        hasWarnFailures) {
        return "ADVISORY_INCOMPLETE";
    }
    const allPassedClean = checks.every((check) => check.passed) &&
        exceptions.length === 0 &&
        !checks.some((check) => check.disabled || check.skipped) &&
        disabledChecks.length === 0 &&
        skippedChecks.length === 0 &&
        !hasWarnFailures;
    if (allPassedClean) {
        return "FULLY_GATED";
    }
    return "ADVISORY_INCOMPLETE";
}
export function buildQAGatedStatus(input) {
    const policy = input.policy ?? DEFAULT_PROOF_GATE_GOVERNANCE_POLICY;
    const exceptions = input.exceptions ?? [];
    const disabledChecks = input.disabledChecks ?? [];
    const skippedChecks = input.skippedChecks ?? [];
    const releaseLabel = computeReleaseLabel({
        checks: input.checks,
        exceptions,
        disabledChecks,
        skippedChecks,
        policy,
    });
    return QAGatedStatusV1Schema.parse({
        schemaVersion: "qa-gated-status.v1.0",
        releaseLabel,
        checks: input.checks,
        exceptions,
        disabledChecks,
        skippedChecks,
        computedAt: new Date().toISOString(),
        policyVersion: policy.policyVersion,
    });
}
//# sourceMappingURL=proof-gate-governance.js.map